import logoImg from 'src/public/img/mh-logo-text.svg';
import { Trans, useTranslation } from 'react-i18next';
import {
    BodyTextMedium,
    ButtonGroup,
    Form,
    FormInputDatePicker,
    FormInputSelect,
    useForm,
    Button,
    HeadingText50,
} from '@modernhealth/vital-components';
import React, { FC, useEffect, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { ExternalLink } from 'src/shared/components/ExternalLink';
import { useDispatch, useSelector } from 'react-redux';
import { getSortedCountries } from 'src/modules/countries/selectors/countrySelectors';
import { clickwrapService, navigationService } from 'src/config/rootService';
import { FieldValues, useFieldArray, UseFormReturn } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Country } from 'src/modules/auth/screens/ConsentFormPage';
import { DisclaimerModal } from 'src/shared/components/DisclaimerModal';
import { config } from 'src/config/config';
import { Dispatch } from 'src/config/rootStore';
import { useAcceptContractsMutation } from 'src/modules/auth/documents/acceptContracts.generated';
import { ConsentFormPageTestId } from 'src/modules/auth/screens/testIds';
import { ConsentCheckbox, ExtendedConsent } from 'src/modules/auth/components/ConsentCheckbox';

export interface ClickwrapConsentFormProps {
    token: string;
    companyCode: string;
    companyName: string;
    firstName: string;
    lastName: string;
    email: string;
    country: string;
    acceptedContractVersions: string[];
    birthDate: string;
    uidb64: string;
    languagePreference: string;
    isSSOFlow: boolean;
}

interface ConsentForm extends FieldValues {
    showDateOfBirth?: boolean;
    country: string;
    birthDate: string | null;
    consents: ExtendedConsent[];
}

const validationSchema = yup.object().shape({
    showDateOfBirth: yup.boolean(),
    country: yup.string().trim().required(),
    consents: yup
        .array()
        .of(
            yup.object().shape({
                name: yup.string(),
                required: yup.boolean(),
                isChecked: yup.bool().when('required', {
                    is: true,
                    then: yup.bool().oneOf([true]).required(),
                }),
            }),
        )
        .required(),
    birthDate: yup.string().nullable().when('showDateOfBirth', {
        is: true,
        then: yup.string().required(),
    }),
});

export const ClickwrapConsentForm: FC<ClickwrapConsentFormProps> = ({
    token,
    companyCode,
    companyName,
    firstName,
    lastName,
    email,
    birthDate,
    uidb64,
    languagePreference,
    isSSOFlow,
    country,
    acceptedContractVersions,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<Dispatch>();
    const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
    const [isFetchingConsents, setIsFetchingConsents] = useState(false);
    const form = useForm<ConsentForm>({
        mode: 'onChange',
        defaultValues: {
            showDateOfBirth: Boolean(token),
            country,
            birthDate,
            consents: [],
        },
        resolver: yupResolver(validationSchema),
    });
    const { fields, replace } = useFieldArray({
        control: form.control,
        name: 'consents',
    });
    const countries = useSelector(getSortedCountries);

    const [acceptContracts] = useAcceptContractsMutation();

    const handleCountrySelect = useCallback(
        async (selectedCountry: string, resetConsents?: boolean) => {
            form.setValue('country', selectedCountry);
            form.setValue('consents', []);
            setIsFetchingConsents(true);
            const result = await clickwrapService.fetchConsents({
                country: selectedCountry,
                language: languagePreference,
            });

            await replace(
                result.contractsData.map(contract => ({
                    ...contract,
                    isChecked:
                        country &&
                        acceptedContractVersions.includes(contract.publishedVersion) &&
                        !resetConsents
                            ? true
                            : false,
                })),
            );
            setIsFetchingConsents(false);
        },
        [form, languagePreference, replace, country, acceptedContractVersions],
    );

    const handleSubmit = (values: ConsentForm) => {
        // EA China registors must accept disclaimer policies 04/13/21
        if (values.country === Country.CHINA) {
            setShowDisclaimerModal(true);
        } else {
            navigateToPasswordFormPage(values);
        }
    };

    const handleGoBack = () => {
        if (token) {
            navigationService.navigate(`/registration?token=${token}`);
        } else if (uidb64) {
            navigationService.navigate(`/registration?uidb64=${uidb64}`);
        } else {
            const state = {
                birthDate,
                firstName,
                lastName,
                companyCode,
                companyName,
                email,
                languagePreference,
            };
            navigationService.navigate('/registration', state);
        }
    };

    const navigateToPasswordFormPage = async (values: ConsentForm) => {
        const acceptedContractVersions = values.consents
            .filter(c => c.isChecked)
            .map(c => c.publishedVersion);

        if (isSSOFlow) {
            await dispatch.user.updateMemberProfile({ country: values.country });
            await acceptContracts({
                variables: {
                    versionIds: acceptedContractVersions,
                },
            });
            navigationService.navigate('/preferred-email');
        } else {
            const state = {
                firstName,
                lastName,
                email,
                token,
                companyCode,
                companyName,
                birthDate: values.birthDate,
                country: values.country,
                uidb64,
                languagePreference,
                acceptedContractVersions,
            };
            navigationService.navigate('/password-form-page', state);
        }
    };

    useEffect(() => {
        if (country && !!acceptedContractVersions?.length) {
            handleCountrySelect(country);
        }
    }, [acceptedContractVersions?.length, country, handleCountrySelect]);

    return (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = config.modernHealthURL;
                }}
            />
            <Container>
                <FormContainer
                    definition={form as unknown as UseFormReturn<FieldValues>}
                    onSubmit={handleSubmit}
                >
                    <HeadingText50>{t('auth:consent_form_page.clickwrap.title')}</HeadingText50>
                    <BodyTextMedium fontColor="gray65">
                        <Trans
                            i18nKey={
                                form.getValues().country === Country.SOUTH_KOREA
                                    ? 'consent_form_page.clickwrap.description.korea'
                                    : 'consent_form_page.clickwrap.description'
                            }
                            ns="auth"
                            components={{
                                privacyNotice: (
                                    <SubtitleLink
                                        openInNewTab
                                        href={`${config.modernHealthURL}/privacy-notice`}
                                    />
                                ),
                                termOfUse: (
                                    <SubtitleLink
                                        openInNewTab
                                        href={`${config.modernHealthURL}/terms-of-use`}
                                    />
                                ),
                            }}
                        />
                    </BodyTextMedium>
                    <FormInputSelect
                        fieldId="country"
                        label={t('auth:consent_form_page.label.country_of_residence')}
                        name="country"
                        onChange={value => handleCountrySelect(value as unknown as string, true)}
                        options={countries.map(({ text, value: optionValue }) => ({
                            value: optionValue,
                            label: text,
                        }))}
                        data-testid={ConsentFormPageTestId.CountryField}
                    />
                    {token && (
                        <FormInputDatePicker
                            fieldId="birthDate"
                            label={t('auth:consent_form_page.label.date_of_birth')}
                            name="birthDate"
                            css={{ marginTop: 16 }}
                        />
                    )}
                    <ConsentCheckbox fetchingConsents={isFetchingConsents} consents={fields} />
                    <ButtonGroup css={{ width: '100%' }}>
                        <Button variant="secondary" onClick={handleGoBack} fluid>
                            {t('auth:consent_form_page.back')}
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            fluid
                            disabled={!form.formState.isValid}
                        >
                            {t('auth:consent_form_page.continue')}
                        </Button>
                    </ButtonGroup>
                </FormContainer>
            </Container>
            <DisclaimerModal
                visible={showDisclaimerModal}
                description={t('shared:disclaimer_modal.description', {
                    returnObjects: true,
                })}
                onClick={() => navigateToPasswordFormPage(form.getValues())}
                buttonText={t('shared:disclaimer_modal.buttonText')}
            />
        </>
    );
};

const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '15em',
    cursor: 'pointer',
    marginRight: 12,
});

const SubtitleLink = styled(ExternalLink)({
    fontSize: 'inherit',
});

const FormContainer = styled(Form)({
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    maxWidth: '500px',
    flexGrow: 1,
});

const Container = styled.div({
    display: 'flex',
    alignItems: 'start',
    paddingTop: '110px',
    justifyContent: 'center',
    flexGrow: 1,
});
